// FinKloz — Equity Management Dashboard
// Top-level container with sub-tabs: Cap Table, Stock Options, SBC, Reports

import React, { useState } from 'react';
import { sans, mono, green, greenLight, greenDark, coral, coralSoft, amber, amberLight, useTheme } from '../../tokens';
import CapTable from './CapTable';
import OptionRegister from './OptionRegister';
import SBCCalculator from './SBCCalculator';

const teal = '#0D9488';

// ─── Reports Tab Content ────────────────────────────────────────────────────────
function EquityReports({ companyId, t }) {
  const reports = [
    {
      title: 'Board Equity Summary',
      desc: 'One-page overview: cap table snapshot, option pool status, recent grants, vesting activity',
      status: 'ready',
      action: () => alert('Board equity summary PDF generation will be available when the export engine is connected.'),
    },
    {
      title: 'Per-Employee Equity Statement',
      desc: 'Individual statement showing grants, vesting progress, estimated current value. For employee distribution.',
      status: 'ready',
      action: () => alert('Select an employee from Stock Options > People view to generate their equity statement.'),
    },
    {
      title: 'Tax Reporting Export (T4 Data)',
      desc: 'Canadian T4 slip data for stock option benefits, exercise records, and taxable amounts. CCPC stock option rules.',
      status: 'coming',
    },
    {
      title: '409A / Fair Market Value Summary',
      desc: 'CCPC equivalent: valuation basis, methodology notes, comparable analysis references. Required for option pricing.',
      status: 'coming',
    },
    {
      title: 'Cap Table CSV Export',
      desc: 'Full cap table with all rounds, ownership percentages (undiluted + fully diluted)',
      status: 'ready',
      action: () => { document.querySelector('[data-tab="cap-table"]')?.click(); },
      note: 'Available on Cap Table tab',
    },
    {
      title: 'Stock Options CSV Export',
      desc: 'All grants with vesting status, fair values, cohorts, exercise prices',
      status: 'ready',
      action: () => { document.querySelector('[data-tab="options"]')?.click(); },
      note: 'Available on Stock Options tab',
    },
  ];

  return (
    <div>
      <div style={{ fontFamily: sans, fontSize: 12, color: t.textSecondary, marginBottom: 16 }}>
        Generate and export equity reports for board meetings, employee communication, tax compliance, and audit.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {reports.map(r => (
          <div key={r.title} style={{
            padding: '16px', background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 8,
            display: 'flex', flexDirection: 'column', gap: 8,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ fontFamily: sans, fontSize: 13, fontWeight: 600, color: t.textPrimary }}>{r.title}</div>
              <span style={{
                fontFamily: sans, fontSize: 9, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                background: r.status === 'ready' ? greenLight : amberLight,
                color: r.status === 'ready' ? greenDark : amber,
              }}>
                {r.status === 'ready' ? 'Ready' : 'Coming Soon'}
              </span>
            </div>
            <div style={{ fontFamily: sans, fontSize: 11, color: t.textSecondary, flex: 1 }}>{r.desc}</div>
            {r.note && <div style={{ fontFamily: sans, fontSize: 9, color: t.textMuted }}>{r.note}</div>}
            {r.action && (
              <button onClick={r.action} style={{
                fontFamily: sans, fontSize: 11, fontWeight: 600, padding: '6px 12px', borderRadius: 4,
                border: `1px solid ${coral}`, background: coralSoft, color: coral,
                cursor: 'pointer', alignSelf: 'flex-start',
              }}>
                Generate
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const SUB_TABS = [
  { id: 'cap-table', label: 'Cap Table' },
  { id: 'options', label: 'Stock Options' },
  { id: 'sbc', label: 'SBC Expense' },
  { id: 'reports', label: 'Reports' },
];

export default function EquityDashboard({ companyId, activeTab: externalTab, onTabChange }) {
  const t = useTheme();
  const [internalTab, setInternalTab] = useState('cap-table');
  const activeTab = externalTab || internalTab;
  const setActiveTab = onTabChange || setInternalTab;

  return (
    <div style={{ padding: '0 24px 24px' }}>

      {/* Sub-tabs — only show if not controlled externally (side nav handles it) */}
      {!externalTab && (
        <div style={{ display: 'flex', gap: 2, marginBottom: 20, borderBottom: `2px solid ${t.border}` }}>
          {SUB_TABS.map(tab => {
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  fontFamily: sans, fontSize: 12, fontWeight: active ? 600 : 400,
                  padding: '8px 16px', border: 'none', cursor: 'pointer',
                  borderBottom: active ? `2px solid ${coral}` : '2px solid transparent',
                  background: 'transparent',
                  color: active ? coral : t.textSecondary,
                  marginBottom: -2,
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Tab content */}
      {activeTab === 'cap-table' && <CapTable companyId={companyId} onNavigateTab={setActiveTab} />}
      {activeTab === 'options' && <OptionRegister companyId={companyId} />}
      {activeTab === 'sbc' && <SBCCalculator companyId={companyId} />}
      {activeTab === 'reports' && <EquityReports companyId={companyId} t={t} />}
    </div>
  );
}
